import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import socketIOClient from 'socket.io-client';
import InboxMessageItem from '../../components/InboxMessageItem';
import InboxMessageItem2 from '../../components/InboxMessageItem2';
import InsurancePicker from '../../components/InsurancePicker';
import ReservationFlow from '../../components/ReservationFlow';
import env from '../../config/env';
import '../../styles/styles.css';
import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';
import './mc-guest-reservation-detail.css';
import './mc-guest.css';

moment.locale('fr');

const socket = socketIOClient(env.apiUrl);

const calendarOpts = {
  sameDay: "[Aujourd'hui à] LT",
  nextDay: '[Demain à] LT',
  nextWeek: 'dddd [à] LT',
  lastDay: '[Hier à] LT',
  lastWeek: '[Dernier] dddd [à] LT',
  sameElse: 'D MMM YYYY',
};

// tone → classes .mc-guest-badge--* (mc-guest.css)
const STATUS_MAP = {
  pending: { label: 'En attente de confirmation', icon: 'bi-clock-history', tone: 'warn' },
  accepted: { label: 'En attente de paiement', icon: 'bi-check-circle-fill', tone: 'primary' },
  rejected: { label: 'Réservation annulée', icon: 'bi-x-circle-fill', tone: 'danger' },
  paid: { label: 'Réservation confirmée', icon: 'bi-bag-check-fill', tone: 'ok' },
  completed: { label: 'Voyage terminé', icon: 'bi-flag-fill', tone: 'ok' },
};

function ReservationGuest() {
  const [conversations, setConversations] = useState([]);
  const [ReservationData, setReservationData] = useState(null);
  const [Message, setMessage] = useState('');
  const [ReviewContent, setReviewContent] = useState('');
  const [reviewSent, setReviewSent] = useState(false);
  const [showModificationModal, setShowModificationModal] = useState(false);
  const [ratingValue, setRatingValue] = useState(5);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [modificationMessage, setModificationMessage] = useState('');
  const [modLoading, setModLoading] = useState(false);
  const [modSuccess, setModSuccess] = useState('');
  const [modError, setModError] = useState('');
  const [NombreJours, setNombreJours] = useState(0);

  const { user } = useUser();

  const userData = user;
  const axios = useAxios();
  const { reservationId } = useParams();
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('mode', 'guest');
  }, []);

  /* ── socket + conversations ── */
  useEffect(() => {
    const onConversation = (conv) => setConversations((prev) => [...prev, conv]);
    const join = () => socket.emit('reservationId', reservationId);

    socket.on('conversation', onConversation);
    socket.on('connect', join);
    if (socket.connected) join();

    axios
      .get('/conversations/' + reservationId)
      .then((r) => {
        if (r.data) setConversations(r.data);
      })
      .catch(console.error);

    return () => {
      socket.off('conversation', onConversation);
      socket.off('connect', join);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId]);

  /* ── réservation ── */
  useEffect(() => {
    axios
      .get('/reservations/getbyid/' + reservationId)
      .then((r) => {
        if (r.data) {
          setReservationData(r.data);
          const ms = new Date(r.data.endDate) - new Date(r.data.startDate);
          setNombreJours(Math.round(ms / (1000 * 3600 * 24)));
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId]);

  /* ── auto-scroll chat (dans le conteneur, sans faire bouger la page) ── */
  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [conversations]);

  const fetchReservation = async () => {
    const r = await axios.get('/reservations/getbyid/' + reservationId);
    setReservationData(r.data);
  };

  /* ── envoi message ── */
  function handleSendMessage() {
    if (!Message.trim() || !ReservationData) return;
    const draft = Message;
    setMessage('');
    axios
      .post('/conversations', {
        authorDriverRole: 1,
        driversId: ReservationData.driverInviteId,
        sentTime: new Date(),
        reservationId: ReservationData.reservationId,
        message: draft,
      })
      .then(() => {
        axios
          .get('/conversations/' + ReservationData.reservationId)
          .then((r) => setConversations(r.data))
          .catch(console.error);
        axios
          .post('/notifications', {
            Titre: 'Nouveau message',
            message: `${userData?.firstName} vous a envoyé un message`,
            userId: userData?.id,
          })
          .catch(console.error);
      })
      .catch(console.error);
  }

  /* ── annulation ── */
  const [showCancelModal, setShowCancelModal] = useState(false);

  function handleAnnulerReservation() {
    setShowCancelModal(true);
  }

  function executerAnnulation() {
    setShowCancelModal(false);

    axios
      .post('/conversations', {
        authorDriverRole: 1,
        driversId: ReservationData.driverInviteId,
        sentTime: new Date(),
        reservationId: ReservationData.reservationId,
        message: "L'invité a annulé la réservation.",
      })
      .then(() => {
        axios
          .get('/conversations/' + ReservationData.reservationId)
          .then((r) => setConversations(r.data))
          .catch(console.error);

        axios
          .put('/reservations/' + ReservationData.reservationId, { status: 'rejected' })
          .then(() => window.location.reload())
          .catch(console.error);

        axios
          .put('/reservationsGain/' + ReservationData.reservationId, { type: '1' })
          .catch(console.error);
      })
      .catch(console.error);
  }

  /* ── modification dates ── */
  const handleSubmitModification = async (e) => {
    e.preventDefault();
    if (!newStartDate || !newEndDate) {
      setModError('Les dates sont obligatoires.');
      return;
    }
    setModLoading(true);
    setModError('');
    setModSuccess('');
    try {
      await axios.post('/askupdateresa', {
        reservationId,
        startDate: newStartDate,
        endDate: newEndDate,
        motif: modificationMessage,
      });
      setModSuccess('Demande envoyée avec succès.');
      setNewStartDate('');
      setNewEndDate('');
      setModificationMessage('');
    } catch {
      setModError('Une erreur est survenue. Merci de réessayer.');
    } finally {
      setModLoading(false);
    }
  };

  /* ── avis ── */
  function handleReviewVehicule() {
    axios
      .post('/reviews', {
        rating: ratingValue,
        content: ReviewContent,
        userId: userData?.id,
        vehiculeId: ReservationData.Vehicle.id,
      })
      .then(() => {
        setReviewSent(true);
        toast.success('Avis ajouté, merci !');
      })
      .catch(console.error);
  }

  const s = STATUS_MAP[ReservationData?.status] ?? {
    label: ReservationData?.status,
    icon: 'bi-circle',
    tone: 'primary',
  };
  const isRejected = ReservationData?.status === 'rejected';
  const isCompleted = ReservationData?.status === 'completed';
  const canSend = !isRejected && !isCompleted && conversations.length > 0;
  const canCancel = !isRejected && !isCompleted;
  const missingDocs =
    Boolean(userData) && (!userData?.PermisConduireDoc || !userData?.PieceIdentityDoc);

  const vehicle = ReservationData?.Vehicle;
  const photo = vehicle?.principalPhotos;

  return (
    <main className="mc-home mc-rg-page">
      <ToastContainer />

      {/* ── Barre supérieure ── */}
      <div className="mc-rg-topbar">
        <div className="container mc-container mc-rg-topbar-in">
          <Link to="/guest/listing/reservations" className="mc-btn mc-btn--secondary mc-btn--sm">
            <i className="bi bi-arrow-left" aria-hidden="true"></i>
            Mes réservations
          </Link>

          {ReservationData && (
            <div className="mc-rg-ref">
              <span className="mc-rg-id">#{reservationId}</span>
              <span className={`mc-guest-badge mc-guest-badge--${s.tone}`}>
                <i className={`bi ${s.icon}`} aria-hidden="true"></i>
                {s.label}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="container mc-container">
        <div className="mc-rg-stack">
          {/* ── Alerte documents ── */}
          {missingDocs && (
            <div className="mc-rg-alert" role="alert">
              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
              <div>
                <strong>Documents manquants :</strong>
                {!userData?.PermisConduireDoc && <span> Permis de conduire</span>}
                {!userData?.PermisConduireDoc && !userData?.PieceIdentityDoc && <span> et</span>}
                {!userData?.PieceIdentityDoc && <span> Carte d'identité</span>}.{' '}
                <Link to="/guest/settings">Compléter mon profil →</Link>
              </div>
            </div>
          )}

          {/* ── Résumé véhicule ── */}
          <section className="mc-rg-card">
            <div className={`mc-rg-summary${photo ? ' has-photo' : ''}`}>
              {photo && (
                <div className="mc-rg-photo">
                  <img src={photo} alt="Véhicule" />
                </div>
              )}

              <div className="mc-rg-info">
                <h1 className="mc-rg-title">
                  {ReservationData ? (
                    `${vehicle.VehicleModel.marque} ${vehicle.VehicleModel.modele}`
                  ) : (
                    <span className="mc-rg-skel" />
                  )}
                </h1>

                <p className="mc-rg-line">
                  <i className="bi bi-calendar3" aria-hidden="true"></i>
                  {ReservationData ? (
                    `${moment(ReservationData.startDate).calendar(null, calendarOpts)} → ${moment(ReservationData.endDate).calendar(null, calendarOpts)}`
                  ) : (
                    <span className="mc-rg-skel" />
                  )}
                  {NombreJours > 0 && (
                    <span className="mc-rg-days">
                      {NombreJours} jour{NombreJours > 1 ? 's' : ''}
                    </span>
                  )}
                </p>

                <p className="mc-rg-line">
                  <i className="bi bi-person" aria-hidden="true"></i>
                  Propriétaire :{' '}
                  <strong>
                    {ReservationData?.Host?.firstName} {ReservationData?.Host?.lastName}
                  </strong>
                </p>

                <div className="mc-rg-actions">
                  {ReservationData?.status === 'accepted' && (
                    <Link
                      to={`/checkout?reservationId=${ReservationData.reservationId}`}
                      className="mc-btn mc-btn--primary"
                    >
                      <i className="bi bi-credit-card" aria-hidden="true"></i>
                      Payer maintenant
                    </Link>
                  )}
                  {canCancel && (
                    <button
                      type="button"
                      className="mc-btn mc-btn--secondary"
                      onClick={() => setShowModificationModal(true)}
                    >
                      <i className="bi bi-pencil" aria-hidden="true"></i>
                      Modifier les dates
                    </button>
                  )}
                  {canCancel && (
                    <button
                      type="button"
                      className="mc-btn mc-rg-danger mc-rg-cancel"
                      onClick={handleAnnulerReservation}
                    >
                      <i className="bi bi-x-circle" aria-hidden="true"></i>
                      Annuler la réservation
                    </button>
                  )}
                </div>
              </div>

              {ReservationData?.status === 'accepted' && (
                <div className="mc-rg-paybar">
                  <i className="bi bi-lightning-fill" aria-hidden="true"></i>
                  Action requise — Payez pour bloquer les dates.
                </div>
              )}
            </div>
          </section>

          {/* ── Assurance ── */}
          {ReservationData?.status === 'accepted' && (
            <section className="mc-rg-card mc-rg-card--pad">
              <InsurancePicker
                nombreJours={NombreJours || 1}
                reservationId={ReservationData.reservationId}
                selectedPlanId={ReservationData.insurancePlanId}
              />
            </section>
          )}

          {/* ── Stepper ── */}
          {ReservationData && (
            <section className="mc-rg-card mc-rg-card--pad">
              <ReservationFlow
                reservation={ReservationData}
                onReservationUpdated={fetchReservation}
              />
            </section>
          )}

          {/* ── Messagerie desktop ── */}
          <div className="d-none d-lg-block">
            <ChatPanel
              conversations={conversations}
              message={Message}
              setMessage={setMessage}
              onSend={handleSendMessage}
              canSend={canSend}
              isRejected={isRejected}
              reservationId={reservationId}
              messagesRef={messagesRef}
              inputRef={inputRef}
            />
          </div>

          {/* ── Avis (voyage terminé) ── */}
          {isCompleted && (
            <section className="mc-rg-card mc-rg-card--pad mc-rg-review">
              <h2 className="mc-rg-review-title">
                <i className="bi bi-star-fill" aria-hidden="true"></i>
                Laisser un avis
              </h2>
              <p className="mc-rg-muted">
                Votre retour aide les autres locataires à choisir en confiance.
              </p>
              <Stack spacing={1}>
                <Rating
                  name="rating-guest"
                  value={ratingValue}
                  onChange={(_, v) => setRatingValue(v ?? 5)}
                  size="large"
                  disabled={reviewSent}
                />
              </Stack>
              <label className="mc-field-label" htmlFor="mc-rg-review-text">
                Votre expérience
              </label>
              <textarea
                id="mc-rg-review-text"
                className="mc-input"
                rows="4"
                placeholder="Décrivez votre expérience…"
                value={ReviewContent}
                onChange={(e) => setReviewContent(e.target.value)}
                disabled={reviewSent}
              />
              <button
                type="button"
                className="mc-btn mc-btn--primary mc-btn--block"
                onClick={handleReviewVehicule}
                disabled={reviewSent}
              >
                <i className={`bi ${reviewSent ? 'bi-check2' : 'bi-send'}`} aria-hidden="true"></i>
                {reviewSent ? 'Avis envoyé' : "Soumettre l'avis"}
              </button>
            </section>
          )}
        </div>
      </div>

      {/* ── FAB messagerie — mobile ── */}
      {!isRejected && (
        <Link to={'/guest/chatbox/' + reservationId} className="mc-rg-fab">
          <i className="bi bi-chat-dots-fill" aria-hidden="true"></i>
          Messagerie
          {conversations.length > 0 && (
            <span className="mc-rg-fab-count">{conversations.length}</span>
          )}
        </Link>
      )}

      {/* ── Modal annulation ── */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
        dialogClassName="mc-rg-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
            Confirmation d'annulation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir annuler ce voyage ? Cette action est irréversible.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="mc-btn mc-btn--ghost"
            onClick={() => setShowCancelModal(false)}
          >
            Fermer
          </button>
          <button type="button" className="mc-btn mc-rg-danger--solid" onClick={executerAnnulation}>
            Confirmer l'annulation
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal modification des dates ── */}
      <Modal
        show={showModificationModal}
        onHide={() => setShowModificationModal(false)}
        centered
        size="lg"
        dialogClassName="mc-rg-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-pencil" aria-hidden="true"></i>
            Demande de modification
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {ReservationData && (
            <div className="mc-rg-mini">
              {photo && <img src={photo} alt="" />}
              <div>
                <p>
                  {vehicle.VehicleModel.marque} {vehicle.VehicleModel.modele}
                </p>
                <small>
                  Actuellement : {moment(ReservationData.startDate).format('D MMM')}
                  {' → '}
                  {moment(ReservationData.endDate).format('D MMM YYYY')}
                </small>
              </div>
            </div>
          )}

          <form id="modificationReservationForm" onSubmit={handleSubmitModification}>
            <h3 className="h6 fw-bold mb-3">Nouvelle proposition de dates</h3>

            <div className="mc-rg-fields">
              <div>
                <label className="mc-field-label" htmlFor="mc-rg-new-start">
                  Date de début
                </label>
                <input
                  id="mc-rg-new-start"
                  type="date"
                  className="mc-input"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="mc-field-label" htmlFor="mc-rg-new-end">
                  Date de fin
                </label>
                <input
                  id="mc-rg-new-end"
                  type="date"
                  className="mc-input"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <label className="mc-field-label" htmlFor="mc-rg-new-msg">
                Message (optionnel)
              </label>
              <textarea
                id="mc-rg-new-msg"
                className="mc-input"
                rows="3"
                placeholder="Expliquez votre demande si besoin…"
                value={modificationMessage}
                onChange={(e) => setModificationMessage(e.target.value)}
              />
            </div>

            {modError && (
              <div className="mc-rg-msg mc-rg-msg--err" role="alert">
                {modError}
              </div>
            )}
            {modSuccess && (
              <div className="mc-rg-msg mc-rg-msg--ok" role="status">
                {modSuccess}
              </div>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="mc-btn mc-btn--ghost"
            onClick={() => setShowModificationModal(false)}
          >
            Fermer
          </button>
          <button
            type="submit"
            form="modificationReservationForm"
            className="mc-btn mc-btn--primary"
            disabled={modLoading}
          >
            {modLoading ? (
              <>
                <span className="mc-rg-spin" aria-hidden="true"></span>
                Envoi…
              </>
            ) : (
              <>
                <i className="bi bi-send" aria-hidden="true"></i>
                Envoyer la demande
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}

/* ══════════════════════════════════════════════════
   Composant Chat (desktop inline)
══════════════════════════════════════════════════ */
function ChatPanel({
  conversations,
  message,
  setMessage,
  onSend,
  canSend,
  isRejected,
  reservationId,
  messagesRef,
  inputRef,
}) {
  return (
    <section className="mc-rg-card mc-rg-chat" aria-label="Messagerie">
      <div className="mc-rg-chat-head">
        <h2 className="mc-rg-chat-title">
          <i className="bi bi-chat-dots" aria-hidden="true"></i>
          Messagerie
        </h2>
        <Link
          to={'/guest/chatbox/' + reservationId}
          className="mc-btn mc-btn--secondary mc-btn--sm"
        >
          Plein écran <i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>
        </Link>
      </div>

      <div className="mc-rg-chat-body" ref={messagesRef}>
        {conversations.length === 0 ? (
          <div className="mc-rg-chat-empty">
            <i className="bi bi-chat-square-dots" aria-hidden="true"></i>
            Aucun message pour le moment.
          </div>
        ) : (
          conversations.map((conv, i) =>
            conv.authorDriverRole === '0' ? (
              <InboxMessageItem key={i} data={conv} />
            ) : (
              <InboxMessageItem2 key={i} data={conv} />
            )
          )
        )}
      </div>

      {isRejected ? (
        <div className="mc-rg-locked">
          <i className="bi bi-lock" aria-hidden="true"></i>
          Messagerie désactivée — réservation annulée.
        </div>
      ) : (
        <form
          className="mc-rg-composer"
          onSubmit={(e) => {
            e.preventDefault();
            onSend();
          }}
        >
          <input
            ref={inputRef}
            className="mc-input"
            placeholder="Écrire un message…"
            aria-label="Votre message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={!canSend}
          />
          <button
            type="submit"
            className="mc-btn mc-btn--primary mc-rg-send"
            disabled={!canSend || !message.trim()}
            aria-label="Envoyer"
            title="Envoyer"
          >
            <i className="bi bi-send-fill" aria-hidden="true"></i>
          </button>
        </form>
      )}
    </section>
  );
}

export default ReservationGuest;
